import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import tw from "twin.macro";
import styled from "styled-components";
import cogoToast from "cogo-toast";

import logo from "images/images/smiles_and_laughters_logo2.png";
import illustration from "images/images/focused-young-black-woman-holding-basket-with-lettuce-on-4177750 (1).jpg";
import googleIconImageSrc from "images/google-icon.png";
import { ReactComponent as SignUpIcon } from "feather-icons/dist/icons/pocket.svg";

import { getUser, HandleCreateUserWithEmail, HandleSignInWithGoogle } from "services/FirebaseAuthService";
import { addPartner, addUser, fetchUsers } from "DBServices/firestoreDBService";
import { minMaxLength, validEmail, passwordStrength } from "services/Validation";
import { APP_NAME } from "../constants";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import Navbar from "components/Navbar";

const Container = styled(ContainerBase)`
    ${tw`min-h-screen text-white font-medium flex justify-center mt-0`}
  background: linear-gradient(135deg, #022c22 0%, #115e59 100%);
`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-2xl flex justify-center flex-1 overflow-hidden`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12 flex flex-col justify-center`;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-4 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold text-gray-800`;
const IllustrationContainer = tw.div`sm:rounded-r-lg flex-1 bg-gray-100 text-center hidden lg:flex justify-center items-center`;
const IllustrationImage = styled.div`
  ${props => `background-image: url("${props.imageSrc}");`}
  ${tw`m-12 xl:m-16 w-full max-w-lg h-80 bg-cover bg-center bg-no-repeat rounded-2xl shadow-md`}
`;

const Form = tw.form`w-full mt-6`;
const Input = tw.input`w-full px-4 py-3 rounded-xl font-medium bg-gray-100 border border-gray-200 placeholder-gray-400 text-sm focus:outline-none focus:border-green-600 focus:bg-white transition duration-200 mt-3 first:mt-0`;
const Select = tw.select`w-full px-4 py-3 rounded-xl font-medium bg-gray-100 border border-gray-200 text-gray-600 text-sm focus:outline-none focus:border-green-600 focus:bg-white transition mt-3 bg-white`;
const Textarea = tw.textarea`w-full px-4 py-3 rounded-xl font-medium bg-gray-100 border border-gray-200 placeholder-gray-400 text-sm focus:outline-none focus:border-green-600 focus:bg-white transition duration-200 mt-3 h-20`;
const SubmitButton = styled.button`
  ${tw`mt-5 tracking-wide font-bold text-white w-full py-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none transform hover:-translate-y-1`}
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  .icon { ${tw`w-5 h-5 -ml-2`} }
  .text { ${tw`ml-3`} }
`;
const ErrorMsg = tw.div`mt-3 text-red-500 text-xs`;

// Social Login buttons
const SocialButtonsContainer = tw.div`flex flex-col items-center w-full mt-4`;
const SocialButton = styled.a`
  ${tw`w-full max-w-xs font-semibold rounded-xl py-3 border text-gray-700 bg-white flex items-center justify-center transition-all duration-200 focus:outline-none focus:shadow-outline text-sm shadow-sm`}
  border: 1px solid #e2e8f0;
  &:hover {
    background-color: #f7fafc;
    border-color: #cbd5e0;
  }
  .iconContainer {
    ${tw`bg-white p-2 rounded-full`}
  }
  .icon {
    ${tw`w-4`}
  }
  .text {
    ${tw`ml-4`}
  }
`;

const DividerTextContainer = tw.div`my-6 border-b border-gray-200 text-center relative w-full`;
const DividerText = tw.div`leading-none px-4 inline-block text-sm text-gray-500 tracking-wide font-medium bg-white transform -translate-y-1/2 absolute left-1/2 -translate-x-1/2`;

// Account type tabs
const TypeTabRow = tw.div`flex w-full mt-6 mb-2 rounded-xl overflow-hidden border border-gray-200`;
const TypeTab = styled.button`
  ${tw`flex-1 py-3 text-sm font-bold transition`}
  ${props => props.active ? `background-color: #059669; color: white;` : tw`bg-gray-100 text-gray-600 hover:bg-gray-200`}
`;

// Partner sub-type tabs
const SubTabRow = tw.div`flex flex-wrap mt-4 mb-2`;
const SubTab = styled.button`
  ${tw`px-3 py-2 text-xs font-bold rounded-full border transition`}
  ${props => props.active
    ? `background-color: #059669; color: white; border-color: #059669; box-shadow: 0 1px 2px rgba(0,0,0,0.1);`
    : tw`bg-white text-gray-600 border-gray-200 hover:border-green-500 hover:text-green-600`}
`;

const SectionLabel = tw.p`text-xs font-bold text-gray-500 uppercase tracking-wider mt-4 mb-1`;
const InfoBox = tw.div`mt-3 p-3 bg-blue-100 border border-blue-200 rounded-lg text-xs text-blue-700`;

const PARTNER_TYPES = [
    { key: "market", label: "🏪 Market", desc: `Sell food items through your own market storefront on ${APP_NAME}.` },
    { key: "farm", label: "🌾 Farm", desc: "Supply fresh produce directly from your farm to customers." },
    { key: "wholesale", label: "📦 Wholesale Store", desc: "Offer bulk items at wholesale prices." },
    { key: "dispatcher", label: "🚚 Dispatcher/Driver", desc: "Handle last-mile delivery for orders." },
    { key: "logistics", label: "🏭 Logistics Partner", desc: "Provide warehousing and transport services." },
];

const SERVICE_AREAS = ["Ikeja", "Lekki", "Yaba", "Gbagada", "Surulere", "Victoria Island", "Ikorodu", "Ajah", "Festac", "Apapa"];

export default function SignupPage() {
    const history = useHistory();
    const auth = getAuth();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [accountType, setAccountType] = useState("customer"); // "customer" | "partner"
    const [partnerType, setPartnerType] = useState("market");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState("");

    // Shared fields
    const [form, setForm] = useState({
        firstname: "", lastname: "", username: "", email: "", phone: "", password: "",
    });

    // Partner-specific fields
    const [partnerForm, setPartnerForm] = useState({
        businessName: "", businessAddress: "", businessPhone: "", businessEmail: "",
        serviceAreas: [], description: "", vehicleType: "", licenseNumber: "",
        warehouseCapacity: "", certifications: "",
    });

    useEffect(() => {
        const u = getUser.auth.currentUser;
        if (u) setIsLoggedIn(true);
    }, []);

    const set = (field) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [field]: v })); };
    const setP = (field) => (e) => { const v = e.target.value; setPartnerForm(f => ({ ...f, [field]: v })); };

    const toggleArea = (area) => {
        setPartnerForm(f => ({
            ...f,
            serviceAreas: f.serviceAreas.includes(area)
                ? f.serviceAreas.filter(a => a !== area)
                : [...f.serviceAreas, area]
        }));
    };

    const validate = () => {
        const e = {};
        if (!validEmail(form.email) === false || !form.email) e.email = "Invalid email address";
        if (minMaxLength(form.firstname, 3)) e.firstname = "Min 3 characters";
        if (minMaxLength(form.lastname, 3)) e.lastname = "Min 3 characters";
        if (minMaxLength(form.username, 3)) e.username = "Min 3 characters";
        if (!form.phone || form.phone.length < 10) e.phone = "Min 10 digits";
        if (minMaxLength(form.password, 6)) e.password = "Min 6 characters";
        else if (passwordStrength(form.password)) e.password = "Include uppercase, number or special character";
        if (accountType === "partner") {
            if (!partnerForm.businessName.trim()) e.businessName = "Business name required";
            if (!partnerForm.businessAddress.trim()) e.businessAddress = "Business address required";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        setServerError("");
        try {
            const userCredential = await HandleCreateUserWithEmail(form);
            const uid = userCredential.user.uid;

            // Save base user doc
            await addUser({
                uid,
                FirstName: form.firstname,
                LastName: form.lastname,
                Username: form.username,
                Email: form.email,
                Phone: form.phone,
                accountType,
                partnerType: accountType === "partner" ? partnerType : "",
                role: "user",
                createdAt: new Date().toISOString(),
            });

            // Save partner application doc
            if (accountType === "partner") {
                await addPartner({
                    uid,
                    name: `${form.firstname} ${form.lastname}`,
                    email: form.email,
                    phone: form.phone,
                    partnerType,
                    businessName: partnerForm.businessName,
                    businessAddress: partnerForm.businessAddress,
                    businessPhone: partnerForm.businessPhone || form.phone,
                    businessEmail: partnerForm.businessEmail || form.email,
                    serviceAreas: partnerForm.serviceAreas,
                    description: partnerForm.description,
                    vehicleType: partnerForm.vehicleType,
                    licenseNumber: partnerForm.licenseNumber,
                    warehouseCapacity: partnerForm.warehouseCapacity,
                    certifications: partnerForm.certifications,
                    verificationStatus: "pending",
                    submittedAt: new Date().toISOString(),
                });
                cogoToast.success("Partner application submitted! We'll review and approve your account shortly.");
                history.push('/partner');
            } else {
                cogoToast.success("Account created successfully!");
                setIsLoggedIn(true);
            }
        } catch (err) {
            setServerError(err.message.replace("Firebase: ", ""));
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoggedIn) {
        return (
            <AnimationRevealPage>
                <Navbar />
                <Container>
                    <Content>
                        <MainContainer>
                            <LogoImage src={logo} />
                            <MainContent>
                                <Heading>You're signed in!</Heading>
                                <SubmitButton onClick={() => history.push("/")} style={{ marginTop: "2rem" }}>
                                    <SignUpIcon className="icon" /><span className="text">Continue to Home</span>
                                </SubmitButton>
                                <SubmitButton onClick={() => { signOut(auth); setIsLoggedIn(false); }} style={{ marginTop: "1rem", backgroundColor: "#ef4444" }}>
                                    <SignUpIcon className="icon" /><span className="text">Logout</span>
                                </SubmitButton>
                            </MainContent>
                        </MainContainer>
                    </Content>
                </Container>
            </AnimationRevealPage>
        );
    }

    const pt = PARTNER_TYPES.find(p => p.key === partnerType);

    return (
        <AnimationRevealPage>
            <Navbar />
            <Container>
                <Content>
                    <MainContainer>
                        <LogoImage src={logo} />
                        <MainContent>
                            <Heading>Sign Up for {APP_NAME}</Heading>

                            <SocialButtonsContainer>
                                <SocialButton 
                                    href="#"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        try {
                                            const result = await HandleSignInWithGoogle();
                                            const user = result.user;
                                            cogoToast.info("Verifying user profile...");
                                            const allUsers = await fetchUsers();
                                            const existingUser = allUsers.find(u => u.uid === user.uid || u.Email === user.email);
                                            
                                            if (existingUser) {
                                                cogoToast.success("Account already exists! Logging in...");
                                                setIsLoggedIn(true);
                                                if (existingUser.accountType === "partner") {
                                                    history.push("/partner");
                                                } else {
                                                    history.push("/");
                                                }
                                            } else {
                                                cogoToast.warn("Redirecting to complete your profile...");
                                                history.push("/complete-profile");
                                            }
                                        } catch (error) {
                                            console.error("Google sign up error:", error);
                                            cogoToast.error(error.message || "Failed to sign up with Google.");
                                        }
                                    }}
                                >
                                    <span className="iconContainer">
                                        <img src={googleIconImageSrc} className="icon" alt=""/>
                                    </span>
                                    <span className="text">Sign Up With Google</span>
                                </SocialButton>
                            </SocialButtonsContainer>

                            <DividerTextContainer>
                                <DividerText>Or Sign up with your e-mail</DividerText>
                            </DividerTextContainer>

                            {/* Account type selector */}
                            <TypeTabRow>
                                <TypeTab type="button" active={accountType === "customer"} onClick={() => setAccountType("customer")}>
                                    🛒 Customer
                                </TypeTab>
                                <TypeTab type="button" active={accountType === "partner"} onClick={() => setAccountType("partner")}>
                                    🤝 Partner / Business
                                </TypeTab>
                            </TypeTabRow>

                            {/* Partner sub-type selector */}
                            {accountType === "partner" && (
                                <>
                                    <SubTabRow>
                                        {PARTNER_TYPES.map(pt => (
                                            <SubTab key={pt.key} type="button" active={partnerType === pt.key} onClick={() => setPartnerType(pt.key)}>
                                                {pt.label}
                                            </SubTab>
                                        ))}
                                    </SubTabRow>
                                    {pt && <InfoBox>{pt.desc}</InfoBox>}
                                </>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <SectionLabel>Personal Information</SectionLabel>
                                <Input placeholder="First name" value={form.firstname} onChange={set("firstname")} />
                                {errors.firstname && <ErrorMsg>{errors.firstname}</ErrorMsg>}
                                <Input placeholder="Last name" value={form.lastname} onChange={set("lastname")} />
                                {errors.lastname && <ErrorMsg>{errors.lastname}</ErrorMsg>}
                                <Input placeholder="Username" value={form.username} onChange={set("username")} />
                                {errors.username && <ErrorMsg>{errors.username}</ErrorMsg>}
                                <Input type="tel" placeholder="Phone Number" value={form.phone} onChange={set("phone")} />
                                {errors.phone && <ErrorMsg>{errors.phone}</ErrorMsg>}
                                <Input type="email" placeholder="Email" value={form.email} onChange={set("email")} />
                                {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
                                <Input type="password" placeholder="Password" value={form.password} onChange={set("password")} />
                                {errors.password && <ErrorMsg>{errors.password}</ErrorMsg>}

                                {/* Partner-specific fields */}
                                {accountType === "partner" && (
                                    <>
                                        <SectionLabel>Business Information</SectionLabel>
                                        <Input placeholder="Business / Market Name *" value={partnerForm.businessName} onChange={setP("businessName")} />
                                        {errors.businessName && <ErrorMsg>{errors.businessName}</ErrorMsg>}
                                        <Input placeholder="Business Address *" value={partnerForm.businessAddress} onChange={setP("businessAddress")} />
                                        {errors.businessAddress && <ErrorMsg>{errors.businessAddress}</ErrorMsg>}
                                        <Input placeholder="Business Phone (if different)" value={partnerForm.businessPhone} onChange={setP("businessPhone")} />
                                        <Input type="email" placeholder="Business Email (if different)" value={partnerForm.businessEmail} onChange={setP("businessEmail")} />
                                        <Textarea placeholder="Brief description of your business / services" value={partnerForm.description} onChange={setP("description")} />

                                        {/* Dispatcher-specific */}
                                        {(partnerType === "dispatcher") && (
                                            <>
                                                <SectionLabel>Delivery Details</SectionLabel>
                                                <Input placeholder="Vehicle Type (e.g. Motorcycle, Van, Truck)" value={partnerForm.vehicleType} onChange={setP("vehicleType")} />
                                                <Input placeholder="Driver's License Number" value={partnerForm.licenseNumber} onChange={setP("licenseNumber")} />
                                            </>
                                        )}

                                        {/* Logistics-specific */}
                                        {partnerType === "logistics" && (
                                            <>
                                                <SectionLabel>Logistics Details</SectionLabel>
                                                <Input placeholder="Warehouse Capacity (e.g. 500 sqm)" value={partnerForm.warehouseCapacity} onChange={setP("warehouseCapacity")} />
                                                <Input placeholder="Certifications (comma-separated)" value={partnerForm.certifications} onChange={setP("certifications")} />
                                            </>
                                        )}

                                        {/* Farm/Wholesale certifications */}
                                        {(partnerType === "farm" || partnerType === "wholesale") && (
                                            <>
                                                <SectionLabel>Certifications / Registrations</SectionLabel>
                                                <Input placeholder="e.g. NAFDAC, CAC, Organic Cert (comma-separated)" value={partnerForm.certifications} onChange={setP("certifications")} />
                                            </>
                                        )}

                                        <SectionLabel>Service Areas</SectionLabel>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.25rem" }}>
                                            {SERVICE_AREAS.map(area => (
                                                <button
                                                    key={area} type="button"
                                                    onClick={() => toggleArea(area)}
                                                    style={{
                                                        padding: "4px 12px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
                                                        background: partnerForm.serviceAreas.includes(area) ? "#10b981" : "#f3f4f6",
                                                        color: partnerForm.serviceAreas.includes(area) ? "white" : "#374151",
                                                        border: partnerForm.serviceAreas.includes(area) ? "1px solid #10b981" : "1px solid #d1d5db"
                                                    }}
                                                >
                                                    {area}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {serverError && <ErrorMsg style={{ fontSize: "0.85rem", marginTop: "0.75rem" }}>{serverError}</ErrorMsg>}

                                <SubmitButton type="submit" disabled={submitting}>
                                    {submitting && <span className="spinner-border spinner-border-sm" />}
                                    <SignUpIcon className="icon" />
                                    <span className="text">
                                        {accountType === "partner" ? "Submit Partner Application" : "Create Account"}
                                    </span>
                                </SubmitButton>

                                <p style={{ marginTop: "1.5rem", fontSize: "0.75rem", color: "#6b7280", textAlign: "center" }}>
                                    By signing up you agree to our{" "}
                                    <a href="/tos" style={{ borderBottom: "1px dotted #9ca3af" }}>Terms of Service</a> and{" "}
                                    <a href="/privacy" style={{ borderBottom: "1px dotted #9ca3af" }}>Privacy Policy</a>.
                                </p>
                                <p style={{ marginTop: "0.75rem", fontSize: "0.8rem", color: "#6b7280", textAlign: "center" }}>
                                    Already have an account?{" "}
                                    <a href="/login" style={{ color: "#10b981", fontWeight: 600 }}>Sign In</a>
                                </p>
                            </Form>
                        </MainContent>
                    </MainContainer>
                    <IllustrationContainer>
                        <IllustrationImage imageSrc={illustration} />
                    </IllustrationContainer>
                </Content>
            </Container>
        </AnimationRevealPage>
    );
}
